import React from 'react';
import BlogLayout from '../../components/BlogLayout';

const RoofClawVsHatch = () => {
    return (
        <BlogLayout
            title="RoofClaw vs Hatch AI: Why a Receptionist Isn't the Same as a Chief of Staff"
            date="Mar 26, 2026"
            readTime={7}
            heroImage="/images/blog/roofclaw-vs-hatch-receptionist-vs-chief-of-staff-hero.png"
            description="Hatch AI and RoofClaw both use AI to help roofing businesses. But they solve completely different problems. Here's how to know which one you're actually missing."
        >
            <p className="lead text-xl text-[#F0EFF4] font-medium">
                If you've been researching AI for your roofing business, you've probably seen Hatch. It's a solid platform. It does what it says on the tin: respond to leads faster, automate follow-up sequences, handle multi-channel outreach. Roofers who were losing jobs because nobody answered texts at 8 PM are winning some of those jobs back. That's real.
            </p>
            <p>
                But there's a category confusion happening in how roofers evaluate these tools. And it's costing some of them the bigger win.
            </p>
            <p className="font-bold text-[#7B61FF] text-xl">
                Hatch is a receptionist. RoofClaw is a chief of staff.
            </p>
            <p>
                Both are valuable. They're not the same job.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16">What Hatch Actually Does (And Does Well)</h2>
            <p>
                Hatch is a communication automation platform. It sits between your lead sources and your sales team. When a lead comes in (from your website, from a Facebook ad, from a CRM trigger) Hatch fires an AI agent that texts, emails, and calls that lead on your behalf.
            </p>
            <p>
                The AI can answer basic questions. It can qualify the lead. It can book an appointment. It can trigger multi-touch follow-up sequences across 30 days. It reports back on which campaigns drove response rates and which didn't.
            </p>
            <p>
                For a company running high-volume lead generation (hundreds of leads per month, multiple salespeople, a separate marketing stack) that's a real operation. The reporting, the compliance across SMS and email, the multi-location support. Hatch is built for that scale.
            </p>
            <p>
                The price reflects it. You're looking at $400-$800 per month to start, billed annually, plus usage fees per conversation and per SMS beyond included limits. That's $5,000-$10,000 per year before you've sent a single message beyond the base tier.
            </p>
            <p>
                This isn't a knock. If that machine is converting leads at the rate Hatch's customers report, the math works.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16">Where the Comparison Breaks Down</h2>
            <p>
                Here's what Hatch doesn't do.
            </p>
            <p>
                <strong className="text-[#F0EFF4]">It doesn't know your business.</strong> Not really. It knows the profile you built in the campaign setup: the name, the FAQ answers, the sequence triggers. Every conversation it has starts from scratch. It doesn't remember that a homeowner called in December about a slow leak and now they're back because there's ice damming. It doesn't connect that pattern to the estimate you sent in January that they never signed.
            </p>
            <p>
                <strong className="text-[#F0EFF4]">It doesn't touch your operations.</strong> Hatch lives in the communication layer. It doesn't know your QuickBooks. It doesn't see your crew schedule. It can't look at a supplier email asking for a revised PO and flag it before it becomes a three-week delay on your flat roof project.
            </p>
            <p>
                <strong className="text-[#F0EFF4]">It doesn't work across the entire business day.</strong> When the conversation gets handed off from the AI to a human, you're back to the humans. And humans take weekends. Humans take sick days. Humans miss emails.
            </p>
            <p>
                Hatch covers the front door. Everything after the front door is still on you.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16">The Different Problem RoofClaw Solves</h2>
            <p>
                RoofClaw is an AI employee. Not an AI campaign runner.
            </p>
            <p>
                The distinction matters because the work that's killing roofing owners isn't the first touch. It's the accumulation of everything that happens after. The estimate that didn't get followed up because the crew called in and the owner spent Tuesday on a roof. The supplier email sitting unopened. The five homeowners who needed a status update on their job and never got one, and two of them have now left Google reviews about it.
            </p>
            <p>
                A RoofClaw agent runs on dedicated hardware: your Mac, in your office, connected to your tools. It has memory across every conversation it's ever had. It knows that Brian called three weeks ago and asked about gutters. It knows your crew schedule because it can see your calendar. It can draft and send emails on your behalf when you approve the workflow. It connects to up to three of your core tools (your CRM, your estimating software, your calendar) and it operates continuously, 24/7, without a seat license per conversation.
            </p>
            <p className="font-bold text-[#7B61FF]">
                The cost is $5,000 once. Hardware included. That's it.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16">The Trade-Off Is Real (Don't Pretend Otherwise)</h2>
            <p>
                Hatch wins on volume and reporting. If you're running a marketing operation with dedicated lead gen staff and you need granular campaign analytics by source and sequence stage, Hatch gives you that dashboard. RoofClaw is not that tool.
            </p>
            <p>
                RoofClaw wins on depth and business-wide intelligence. If you're a $1M-$10M roofing operator who needs an AI that actually knows your business (remembers your clients, touches your operations, doesn't require you to build a new campaign every time you want it to do something) RoofClaw is built for that problem.
            </p>
            <p>
                Most roofers in that range don't need a marketing automation platform. They need the day-to-day grind handled. The emails answered. The follow-up sent. The schedule confirmed. The admin that's currently happening at 11 PM moved to 7 AM while they sleep.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16">One Question Worth Asking Yourself</h2>
            <p>
                Before you sign an annual contract with any AI tool, ask: what problem am I actually trying to solve?
            </p>
            <p>
                If the answer is "I need to respond to 500 leads per month faster than my competition," Hatch is a strong option. Look hard at their pricing tiers and usage caps. Make sure the math works at your lead volume.
            </p>
            <p>
                If the answer is "I need my business to run smoother when I'm not watching it," that's a different conversation. That's the one RoofClaw was built for.
            </p>
            <blockquote>
                A receptionist keeps the front door moving. A chief of staff keeps the whole operation from falling apart when you're on the roof.
            </blockquote>
            <p>
                Know which one you're missing.
            </p>

            <hr className="my-16 border-[#F0EFF4]/10" />

            <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-[2rem] p-10 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#7B61FF] opacity-10 blur-[100px] rounded-full mix-blend-screen pointer-events-none"></div>
                <h2 className="text-3xl font-heading font-bold mb-6">See what a chief of staff actually looks like</h2>
                <p className="text-lg text-[#F0EFF4]/80 mb-6">
                    RoofClaw is an AI employee built specifically for roofing businesses. Dedicated hardware, persistent memory, connected to your tools, running 24/7. One price, no monthly seat licenses.
                </p>
                <p className="text-xl font-drama italic text-[#7B61FF]">
                    If you need the front door covered, Hatch does that well. If you need the whole house running while you're on the roof, that's a different tool.
                </p>
                <p className="text-lg text-[#F0EFF4]/80 mt-6">
                    $5,000 USD. Hardware included. Book a free 30-minute consult at roofclaw.com.
                </p>
            </div>
        </BlogLayout>
    );
};

export default RoofClawVsHatch;
